import { useState } from 'react';
import { ApiError, createRoom, joinRoom } from '../api';
import { ErrorBanner } from '../components/ErrorBanner';
import { randomPlayerName } from '../randomName';
import type { StoredSession } from '../storage';

export interface CreateJoinProps {
  initialCode: string | null;
  onJoined: (session: StoredSession) => void;
  onBack: () => void;
}

export function CreateJoin({ initialCode, onJoined, onBack }: CreateJoinProps): JSX.Element {
  const [mode, setMode] = useState<'join' | 'create'>(initialCode ? 'join' : 'create');
  const [name, setName] = useState('');
  /* Generated ONCE per mount (lazy useState initialiser, not on every
   * render) and used for BOTH the placeholder and the empty-field fallback,
   * so the name shown in the greyed-out field is literally the name you get
   * if you submit blank -- no surprise rename after the fact. Replaces the
   * old "Player 1"/"Player 2" literals, which read as placeholders rather
   * than names and made the leaderboard's team column useless (every
   * anonymous row said "Player 1"). */
  const [fallbackName] = useState(randomPlayerName);
  const [code, setCode] = useState(initialCode ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const chosen = name.trim() || fallbackName;
      const res = await createRoom(chosen);
      onJoined({ code: res.code, token: res.player_token, seat: 1, name: chosen });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create a room. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function submitJoin(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setError(null);
    const trimmedCode = code.trim();
    if (trimmedCode.length === 0) {
      setError('Enter a team code.');
      return;
    }
    setBusy(true);
    try {
      const chosen = name.trim() || fallbackName;
      const res = await joinRoom(trimmedCode, chosen);
      onJoined({ code: res.code, token: res.player_token, seat: 2, name: chosen });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join that room. Check the code and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen createjoin-screen" data-testid="screen-createjoin">
      <div className="screen-header">
        <button type="button" className="btn btn-link back-link" onClick={onBack}>
          <span className="back-link-arrow" aria-hidden>
            ←
          </span>
          Back
        </button>
      </div>
      <div className="screen-body">
        {/* Pixel-font usage policy (critique cross-cutting #3): 3 words --
            body sans, not the identity pixel font (see `.title-sans` in
            index.css). */}
        <h1 className="createjoin-title title-sans">Create or join</h1>
        <p className="dim createjoin-tag">Start a fresh maze, or drop in on a friend's with their team code.</p>
        <div className="createjoin-panel">
          <div className="tabs" role="tablist" aria-label="Create or join a room">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'create'}
              className={`tab${mode === 'create' ? ' tab-active' : ''}`}
              onClick={() => setMode('create')}
            >
              Create room
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'join'}
              className={`tab${mode === 'join' ? ' tab-active' : ''}`}
              onClick={() => setMode('join')}
            >
              Join with code
            </button>
          </div>

          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

          {mode === 'create' ? (
            <form className="form" onSubmit={submitCreate} data-testid="form-create">
              <label className="field">
                <span>Your name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={32}
                  placeholder={fallbackName}
                  autoComplete="off"
                />
              </label>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Creating...' : 'Create room'}
              </button>
            </form>
          ) : (
            <form className="form" onSubmit={submitJoin} data-testid="form-join">
              <label className="field">
                <span>Team code</span>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  maxLength={16}
                  placeholder="MATES1"
                  autoComplete="off"
                  data-testid="input-code"
                />
              </label>
              <label className="field">
                <span>Your name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={32}
                  placeholder={fallbackName}
                  autoComplete="off"
                />
              </label>
              <button type="submit" className="btn btn-primary" disabled={busy} data-testid="btn-join">
                {busy ? 'Joining...' : 'Join room'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
